import React, { useEffect, useState } from "react";
import { Icon, Label, Menu, Message } from "semantic-ui-react";
import { toast } from "react-toastify";
import RootTemplate from "../../components/RootTemplate";
import PageHeader from "../../components/PageHeader";
import {
    downloadReportArtifact,
    exportDetailedOrderReport,
    getOrderDashboard,
    getRolePerformance,
} from "../../services/reporting.service";
import {
    OrderDashboardResponse,
    ReportFilters,
    SortDirection,
} from "../../types/models/Reporting";
import DashboardFilters from "./components/DashboardFilters";
import DetailedOrdersReport from "./components/DetailedOrdersReport";
import ExecutiveDashboard from "./components/ExecutiveDashboard";
import RolePerformance from "./components/RolePerformance";
import SalesReport from "./components/SalesReport";
import { formatReportDate } from "./reportingFormatters";
import "./styles.css";

interface UserOption {
    key: string;
    value: string;
    text: string;
}

const normalizeFilters = (filters: ReportFilters): ReportFilters => ({
    ...filters,
    search: filters.search?.trim() || undefined,
    asOf: filters.asOf ? new Date(filters.asOf).toISOString() : undefined,
});

const toInputDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const currentMonthFilters = (): ReportFilters => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
        from: toInputDate(firstDay),
        to: toInputDate(today),
    };
};

const Dashboard = () => {
    const [draftFilters, setDraftFilters] = useState<ReportFilters>(currentMonthFilters);
    const [appliedFilters, setAppliedFilters] = useState<ReportFilters>(currentMonthFilters);
    const [dashboard, setDashboard] = useState<OrderDashboardResponse>();
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string>();
    const [reloadToken, setReloadToken] = useState(0);
    const [activeSection, setActiveSection] = useState<"executive" | "performance" | "report" | "sales">("executive");
    const [advisorOptions, setAdvisorOptions] = useState<UserOption[]>([]);
    const [installerOptions, setInstallerOptions] = useState<UserOption[]>([]);
    const [reportSortProperty, setReportSortProperty] = useState("riskScore");
    const [reportSortDirection, setReportSortDirection] = useState<SortDirection>("desc");

    useEffect(() => {
        let mounted = true;
        const loadOptions = async () => {
            try {
                const [advisors, installers] = await Promise.all([
                    getRolePerformance("ADVISOR", {}, { page: 0, size: 100, sort: "fullName,asc" }),
                    getRolePerformance("INSTALLER", {}, { page: 0, size: 100, sort: "fullName,asc" }),
                ]);
                if (!mounted) return;
                const toOptions = (users: typeof advisors.data.users): UserOption[] => users.map((user) => ({
                    key: user.userId,
                    value: user.userId,
                    text: `${user.fullName || user.username}${user.active ? "" : " · Inactivo"}`,
                }));
                setAdvisorOptions(toOptions(advisors.data.users));
                setInstallerOptions(toOptions(installers.data.users));
            } catch (optionsError) {
                // Los filtros siguen siendo utilizables aunque no se precargue un catálogo.
            }
        };
        loadOptions();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        const fetchDashboard = async () => {
            setLoading(true);
            setError(undefined);
            try {
                const response = await getOrderDashboard(appliedFilters);
                if (mounted) setDashboard(response.data);
            } catch (requestError: any) {
                if (mounted) setError(requestError.message || "No fue posible cargar la vista gerencial.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchDashboard();
        return () => { mounted = false; };
    }, [appliedFilters, reloadToken]);

    const validateFilters = () => {
        if (draftFilters.from && draftFilters.to && draftFilters.from > draftFilters.to) {
            toast.error("La fecha inicial no puede ser posterior a la fecha final.");
            return false;
        }
        if (draftFilters.asOf && new Date(draftFilters.asOf).getTime() > Date.now() + 60000) {
            toast.error("La fecha de corte no puede estar en el futuro.");
            return false;
        }
        return true;
    };

    const applyFilters = () => {
        if (!validateFilters()) return;
        setAppliedFilters(normalizeFilters(draftFilters));
        setReloadToken((value) => value + 1);
    };

    const applyDateRange = (from: string, to: string) => {
        const filters = normalizeFilters({ ...draftFilters, from, to });
        setDraftFilters({ ...draftFilters, from, to });
        setAppliedFilters(filters);
        setReloadToken((value) => value + 1);
    };

    const resetFilters = () => {
        setDraftFilters({});
        setAppliedFilters({});
        setReloadToken((value) => value + 1);
    };

    const showPriorityQueue = () => {
        if (!validateFilters()) return;
        const priorityFilters = { ...draftFilters, attentionOnly: true };
        setDraftFilters(priorityFilters);
        setAppliedFilters(normalizeFilters(priorityFilters));
        setActiveSection("report");
        setReportSortProperty("riskScore");
        setReportSortDirection("desc");
        setReloadToken((value) => value + 1);
    };

    const exportReport = async () => {
        if (!validateFilters()) return;
        const filters = normalizeFilters(draftFilters);
        setExporting(true);
        try {
            setAppliedFilters(filters);
            const response = await exportDetailedOrderReport(
                filters,
                `${reportSortProperty},${reportSortDirection}`
            );
            await downloadReportArtifact(response.data);
            toast.success(`Reporte generado: ${response.data.fileName}`);
        } catch (requestError: any) {
            toast.error(requestError.message || "No fue posible generar el reporte.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <RootTemplate>
            <div className="admin-dashboard">
                <PageHeader
                    title="Dashboard administrativo"
                    subtitle="Ventas, cobranza, operación y calidad de información"
                    actionComponents={dashboard && (
                        <Label basic color="grey" className="dashboard-cutoff-label">
                            <Icon name="clock outline" /> Corte {formatReportDate(dashboard.cutoff, true)}
                        </Label>
                    )}
                />

                <DashboardFilters
                    filters={draftFilters}
                    advisorOptions={advisorOptions}
                    installerOptions={installerOptions}
                    loading={loading}
                    exporting={exporting}
                    onChange={setDraftFilters}
                    onApplyRange={applyDateRange}
                    onApply={applyFilters}
                    onReset={resetFilters}
                    onShowPriority={showPriorityQueue}
                    onExport={exportReport}
                />

                <Menu pointing secondary stackable className="dashboard-main-tabs">
                    <Menu.Item active={activeSection === "executive"} onClick={() => setActiveSection("executive")}>
                        <Icon name="chart bar" /> Vista gerencial
                    </Menu.Item>
                    <Menu.Item active={activeSection === "performance"} onClick={() => setActiveSection("performance")}>
                        <Icon name="users" /> Desempeño por rol
                    </Menu.Item>
                    <Menu.Item active={activeSection === "report"} onClick={() => setActiveSection("report")}>
                        <Icon name="file alternate outline" /> Reporte operativo
                        {appliedFilters.attentionOnly && <Label color="red" size="mini">Atención</Label>}
                    </Menu.Item>
                    <Menu.Item active={activeSection === "sales"} onClick={() => setActiveSection("sales")}>
                        <Icon name="chart line" /> Reporte de ventas
                    </Menu.Item>
                </Menu>

                {error && activeSection !== "executive" && (
                    <Message warning content={`La vista gerencial no pudo actualizarse: ${error}`} />
                )}

                {activeSection === "executive" && (
                    <ExecutiveDashboard
                        data={dashboard}
                        filters={appliedFilters}
                        loading={loading}
                        error={error}
                        onOpenPriority={showPriorityQueue}
                    />
                )}
                {activeSection === "performance" && (
                    <RolePerformance filters={appliedFilters} reloadToken={reloadToken} />
                )}
                {activeSection === "report" && (
                    <DetailedOrdersReport
                        filters={appliedFilters}
                        reloadToken={reloadToken}
                        sortProperty={reportSortProperty}
                        sortDirection={reportSortDirection}
                        onSortChange={(property, direction) => {
                            setReportSortProperty(property);
                            setReportSortDirection(direction);
                        }}
                    />
                )}
                {activeSection === "sales" && (
                    <SalesReport filters={appliedFilters} reloadToken={reloadToken} />
                )}
            </div>
        </RootTemplate>
    );
};

export default Dashboard;
